import { useApolloClient } from '@apollo/client';
import { useMemo } from 'react';
import { useAuthContext } from 'src/auth/useAuthContext';
import type { ExtendedRoles } from 'src/features/workspace/WorkspaceShareMemberRoleSelectProps';
import type {
  Board,
  Table,
  TableColumns,
  TableRow,
  TableRowComment,
} from 'src/generated/graphql';
import { useUserFeatureRole } from 'src/hooks/useUserFeatureRole';
import { useSocketEvent, useSocketSubscribe } from 'src/socket';
import {
  onBoardUpdated,
  onTableColumnCreated,
  onTableRowBulkUpdate,
  onTableRowCreated,
  onTableRowDeleted,
  onTableRowUpdated,
} from 'src/socket/listeners/board';
import { useTableViewHeaderState } from '../shared/BoardUIStatePersistence/useTableViewHeaderUIState';
import {
  addTableColumnToCache,
  addTaskToBoardCache,
  removeTableRowsFromCache,
  updateBoardRolesToCache,
  updateColsTasksFromCache,
} from '../shared/apolloCache';
import { useBoardRoleCanView } from '../shared/useBoardRoleCanView';
import { useBoardContext } from './BoardContext';
import { useBoardTableContext } from './TablePage/BoardTableContextProvider';

export const parseBoardToGql = (board: Board): Board => {
  return {
    ...board,
    tables: (board.tables || []).map((table) => ({
      ...table,
      __typename: 'Table',
    })),
    __typename: 'Board',
  };
};
const parseRowToGql = (row: TableRow): TableRow => {
  return {
    ...row,
    columns: (row.columns || []).map((column) => ({
      ...column,
      __typename: 'TableRowColumn',
    })),
    __typename: 'TableRow',
    hasComments: row.hasComments || false,
    unreadComments: row.unreadComments || 0,
    hasDescription: row.hasDescription || false,
  };
};
export const parseTableRowCommentToGql = (
  comment: TableRowComment,
): TableRowComment => {
  return {
    ...comment,
    __typename: 'TableRowComment',
    edited: comment.edited || false,
    mediaRef: comment.mediaRef || null,
    fileName: comment.fileName || null,
    fileSize: comment.fileSize || null,
    mediaThumbnailRef: comment.mediaThumbnailRef || null,
  };
};
const parseColumnToGql = (column: TableColumns): TableColumns => {
  return {
    ...column,
    __typename: 'TableColumns',
    options: column.options || [],
    system: column.system || false,
    function: column.function || null,
    resizable: column.resizable || true,
    columnRefValue: column.columnRefValue || null,
    linkingColumnRef: column.linkingColumnRef || null,
  };
};

export const BoardSocketListeners = ({
  boardId,
  viewId,
}: { boardId: string; viewId: string }) => {
  const { user } = useAuthContext();
  const body = useMemo(() => ({ boardId }), [boardId]);
  const { cache } = useApolloClient();
  const { tableViewFilter } = useTableViewHeaderState(boardId, viewId);

  const { board } = useBoardContext();
  const { table } = useBoardTableContext();

  const { roles } = useUserFeatureRole<ExtendedRoles, any>({
    feature: board,
    memberId: user?._id as string,
  });

  useSocketSubscribe({
    subscribe: '/board/subscribe',
    unsubscribe: '/board/unsubscribe',
    body,
  });

  const canView = (row: TableRow) =>
    useBoardRoleCanView({
      userId: user?._id || '',
      table: table as Table,
      roles,
      row,
    });

  const removeRow = (row: TableRow) =>
    removeTableRowsFromCache(cache, {
      boardId,
      tableId: row.tableId,
      rowIds: [row._id],
    });

  const addRow = (row: TableRow) =>
    addTaskToBoardCache(
      cache,
      {
        boardId,
        tableId: row.tableId,
        rows: [parseRowToGql(row)],
      },
      tableViewFilter,
    );

  useSocketEvent(
    onBoardUpdated((updatedBoard) => {
      updateBoardRolesToCache(cache, { updatedBoard });
    }),
  );

  useSocketEvent(
    onTableRowUpdated((row) => {
      // condition to prevent socket from origin
      // if (row.updatedBy === user?._id) return;
      if (!canView(row)) return removeRow(row);
      addRow(row);
    }),
  );

  useSocketEvent(
    onTableRowCreated((row) => {
      if (row.ownerId === user?._id) return;
      if (!canView(row)) return removeRow(row);
      addRow(row);
    }),
  );

  useSocketEvent(
    onTableRowDeleted(({ rowIds, tableId }) => {
      removeTableRowsFromCache(cache, {
        boardId,
        tableId,
        rowIds,
      });
    }),
  );

  useSocketEvent(
    onTableColumnCreated((column) => {
      addTableColumnToCache(cache, {
        boardId,
        tableColumn: parseColumnToGql(column),
      });
    }),
  );

  useSocketEvent(
    onTableRowBulkUpdate(({ tableId, rowIds, columns }) => {
      updateColsTasksFromCache(cache, {
        boardId,
        tableId,
        rowIds,
        columns,
      });
    }),
  );

  return null;
};
